import { StockWarehouse } from "../../queries/useSearchTiresByVehicle";

const TIRE_LINK_PROVIDER = "TireLink";
const TDG_PROVIDER = "Tdg";

export const AVAILABILITY_LABELS: Record<number, string> = {
  1: "Local DC",
  2: "Local+",
  3: "Nationwide",
};

/** The fields of a tire or wheel stock record that real-time rows are built from. */
export type RealTimeStockItem = {
  stockLocationId: string;
  stockQtyLocal: string;
  stockQtyLocalPlus: string;
  warehouses?: StockWarehouse[];
};

export type RealTimeRow =
  | {
      availability: number;
      key: string;
      kind: "warehouse";
      name: string;
      qty: number;
    }
  | {
      key: string;
      kind: "tireLink";
      local: number | string;
      localPlus: number | string;
    };

const sumWarehouses = (
  warehouses: StockWarehouse[],
  availability: number
): number =>
  warehouses
    .filter((warehouse) => warehouse.availability === availability)
    .reduce((acc, { qtyAvailable }) => acc + Number(qtyAvailable), 0);

export const hasProviderBreakdown = (item: RealTimeStockItem): boolean =>
  (item.warehouses ?? []).some(({ provider }) => !!provider);

/**
 * Real-time stock merges every live integration into one record per part.
 * When the API tags warehouses with their provider, TireLink keeps its
 * aggregate row and each Tire Discounter warehouse gets its own row.
 */
export const buildRealTimeRows = (
  rltStock: RealTimeStockItem[]
): RealTimeRow[] =>
  rltStock.flatMap((item): RealTimeRow[] => {
    const fallback: RealTimeRow[] = [
      {
        key: item.stockLocationId,
        kind: "tireLink",
        local: item.stockQtyLocal,
        localPlus: item.stockQtyLocalPlus,
      },
    ];

    if (!hasProviderBreakdown(item)) {
      return fallback;
    }

    const warehouses = item.warehouses ?? [];
    const rows: RealTimeRow[] = [];

    const tireLink = warehouses.filter(
      ({ provider }) => provider === TIRE_LINK_PROVIDER
    );
    const tireLinkLocal = sumWarehouses(tireLink, 1);
    const tireLinkLocalPlus = sumWarehouses(tireLink, 2);
    if (tireLinkLocal + tireLinkLocalPlus > 0) {
      rows.push({
        key: `${item.stockLocationId}-tirelink`,
        kind: "tireLink",
        local: tireLinkLocal,
        localPlus: tireLinkLocalPlus,
      });
    }

    warehouses
      .filter(
        ({ provider, qtyAvailable }) =>
          provider === TDG_PROVIDER && Number(qtyAvailable) > 0
      )
      .sort(
        (a, b) =>
          a.availability - b.availability ||
          Number(b.qtyAvailable) - Number(a.qtyAvailable)
      )
      .forEach((warehouse) => {
        rows.push({
          availability: warehouse.availability,
          key: `${item.stockLocationId}-tdg-${
            warehouse.id ?? warehouse.code ?? warehouse.name
          }`,
          kind: "warehouse",
          name: `Tire Discounter - ${warehouse.name}`,
          qty: Number(warehouse.qtyAvailable),
        });
      });

    return rows.length ? rows : fallback;
  });

/** Header counts: TireLink's aggregate and Tire Discounter's warehouse total. */
export const countRealTimeRows = (
  rows: RealTimeRow[]
): { tdg: number; tireLink: number } =>
  rows.reduce(
    (acc, row) =>
      row.kind === "tireLink"
        ? {
            ...acc,
            tireLink: acc.tireLink + Number(row.local) + Number(row.localPlus),
          }
        : { ...acc, tdg: acc.tdg + row.qty },
    { tdg: 0, tireLink: 0 }
  );
